import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Spin,
  Upload,
  Button,
  Typography,
  Space,
  theme,
  ConfigProvider,
  message,
  Tag,
  Image,
  Select,
} from "antd";
import {
  CloudUploadOutlined,
  PaperClipOutlined,
  CloseOutlined,
  DownloadOutlined,
  CheckOutlined,
  LockOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  ExclamationCircleFilled,
  SyncOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  RightOutlined,
  DeleteOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import PracticeDataRequestService, {
  CsvTemplateOptions,
  CsvTemplateType,
  getEnumTypeValue,
  EmailProviderType,
  UserCreationMethod,
  LoginMode,
  EmailTemplateSubType,
  EmailTemplateSubTypeOptions,
  PracticeDataRequestStatus,
} from "../../Services/PracticeDataRequestService";
import logoImg from "../../images/actingofficelogo.svg";
import {
  FORM_STEPS,
  getRequiredFieldNames,
  ALL_SECTIONS,
  NoReplyEmailOption,
  IFieldConfig,
  getRequiredCsvTemplates,
  isTemplateRequired,
} from "./PublicClientDataRequestFormConfig";
import {
  DynamicSection,
  CustomRenderers,
} from "./PublicClientDataRequestFieldRenderer";
import { Utils } from "../../Utilities/Utils";

export { EmailTemplateSubType, EmailTemplateSubTypeOptions };

const { Text } = Typography;
const { Dragger } = Upload;

// Ordered subtab keys across all steps, derived once from the schema.
const SECTION_ORDER = ALL_SECTIONS.map((s) => s.key);
const getSectionStepId = (sectionKey: string) =>
  FORM_STEPS.find((step) => step.sections.some((s) => s.key === sectionKey))
    ?.id;
const getFirstSectionKey = (stepId: number) =>
  FORM_STEPS.find((step) => step.id === stepId)?.sections[0]?.key;

// High-fidelity default light theme configuration
const forcedLightTheme = {
  algorithm: theme.defaultAlgorithm,
  token: {
    colorPrimary: "#0078D4", // Slate-Blue primary
    colorSuccess: "#059669", // Emerald green
    colorWarning: "#D97706", // Warm Amber
    colorError: "#DC2626", // Premium Red
    colorInfo: "#3B82F6", // Light Info Blue
    colorTextBase: "#1F2937", // Deep slate text
    colorBgBase: "#FFFFFF", // Pure white background
    fontFamily:
      "'Google Sans Flex', 'Google Sans', 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    fontSize: 13,
    borderRadius: 6,
    wireframe: false,
  },
  components: {
    Button: {
      controlHeight: 32,
      controlHeightSmall: 26,
      borderRadius: 8,
    },
    Input: {
      colorBorder: "#E5E7EB",
      activeBorderColor: "#0078D4",
      borderRadius: 6,
    },
    Table: {
      headerBg: "#FFFFFF",
      headerColor: "#4B5563",
      headerSplitColor: "transparent",
      borderColor: "#F3F4F6",
      rowHoverBg: "#EFF6FF",
      padding: 8,
    },
    Menu: {
      itemBg: "transparent",
      itemSelectedBg: "#EFF6FF",
      itemSelectedColor: "#0078D4",
      itemHoverBg: "#F3F4F6",
      itemColor: "#4B5563",
      borderRadius: 6,
    },
  },
};

const PublicClientDataRequestContent: React.FC = () => {
  const { token: routeToken } = useParams<{ token: string }>();
  const [form] = Form.useForm();
  const { token: themeToken } = theme.useToken();

  // Themed styles mapped directly to global Ant Design design tokens
  const styles = {
    pageBg: themeToken.colorBgLayout || "#f8fafc",
    cardBg: themeToken.colorBgContainer || "#ffffff",
    secondaryBg: themeToken.colorBgLayout || "#f8fafc",
    borderColor: themeToken.colorBorder,
    borderLightColor: themeToken.colorBorderSecondary || themeToken.colorBorder,
    textPrimary: themeToken.colorText,
    textTitle: themeToken.colorTextHeading || themeToken.colorText,
    textSecondary: themeToken.colorTextSecondary,
    tabActiveBg: themeToken.colorPrimaryBg,
    chipBg: themeToken.colorPrimaryBg,
    chipBorder: themeToken.colorBorderSecondary || themeToken.colorBorder,
    chipText: themeToken.colorPrimary,
    chipCloseBg: themeToken.colorFillContent || themeToken.colorPrimaryBg,
    btnSecondaryBg: themeToken.colorBgContainer || "#ffffff",
    btnSecondaryBorder: themeToken.colorBorder,
    btnSecondaryText: themeToken.colorText,
    btnPrimaryBg: themeToken.colorPrimary,
    btnPrimaryBorder: themeToken.colorPrimary,
    successColor: themeToken.colorSuccess || "#16a34a",
    successBg: themeToken.colorSuccessBg || "#dcfce7",
    errorColor: themeToken.colorError || "#dc2626",
    errorBg: themeToken.colorErrorBg || "#fef2f2",
    errorBorder: themeToken.colorErrorBorder || "#fca5a5",
  };

  // Navigation & Loading State
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [invalidToken, setInvalidToken] = useState(false);

  const getInitialStep = (): number => {
    try {
      if (typeof window !== "undefined" && routeToken) {
        const saved =
          localStorage.getItem(`pdr_step_${routeToken}`) ||
          sessionStorage.getItem(`pdr_step_${routeToken}`);
        if (saved) {
          const parsed = parseInt(saved, 10);
          if (parsed === 1 || parsed === 2) return parsed;
        }
      }
    } catch {}
    return 1;
  };

  const getInitialSubTab = (step: number): string => {
    try {
      if (typeof window !== "undefined" && routeToken) {
        const saved =
          localStorage.getItem(`pdr_subtab_${routeToken}`) ||
          sessionStorage.getItem(`pdr_subtab_${routeToken}`);
        if (saved && SECTION_ORDER.includes(saved)) {
          const subTabStep = getSectionStepId(saved);
          if (subTabStep === step) {
            return saved;
          }
        }
      }
    } catch {}
    return getFirstSectionKey(step) || "basic_info";
  };

  const [currentStep, setCurrentStep] = useState<number>(getInitialStep);
  const [activeSubTab, setActiveSubTab] = useState<string>(() =>
    getInitialSubTab(getInitialStep()),
  );
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);

  // OTP State
  const [requiresOtp, setRequiresOtp] = useState(false);
  const [otpEmail, setOtpEmail] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [otpValue, setOtpValue] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);

  const [otherFileName, setOtherFileName] = useState("");
  const [newEmailTemplateSubType, setNewEmailTemplateSubType] =
    useState<string>(EmailTemplateSubTypeOptions[0].value);
  const [emailSubTypeOptions, setEmailSubTypeOptions] = useState<
    { value: string; label: string }[]
  >(
    EmailTemplateSubTypeOptions.map((o) => ({
      value: String(o.value),
      label: o.label,
    })),
  );
  const [emailSearchText, setEmailSearchText] = useState("");
  const [rawFiles, setRawFiles] = useState<{ [key: string]: File }>({});

  const normalizeFile = (file: any) => {
    const rawType =
      file?.Type !== undefined
        ? file?.Type
        : file?.type !== undefined
          ? file?.type
          : file?.importType;
    const enumType = getEnumTypeValue(rawType);
    const fileName =
      file?.Name ||
      file?.name ||
      file?.File?.Name ||
      file?.File?.name ||
      file?.file?.name ||
      file?.file?.Name ||
      (file?.rawFile as File)?.name ||
      "";

    let rawFileObj = file?.File || file?.file;
    if (!rawFileObj && (file?.FileUrl || file?.fileUrl)) {
      rawFileObj = { Path: file?.FileUrl || file?.fileUrl, Name: fileName };
    }

    const fileObj = rawFileObj
      ? {
          id: rawFileObj.Id || rawFileObj.id,
          name: rawFileObj.Name || rawFileObj.name || fileName,
          path: rawFileObj.Path || rawFileObj.path,
          contentType: rawFileObj.ContentType || rawFileObj.contentType,
          length: rawFileObj.Length || rawFileObj.length,
        }
      : null;

    const rawF =
      file?.rawFile ||
      file?.originFileObj ||
      (file?.file instanceof File ? file.file : null);

    const rawSubType =
      file?.subType ??
      file?.SubType ??
      file?.emailTemplateSubType ??
      file?.EmailTemplateSubType;
    const subTypeVal =
      rawSubType &&
      rawSubType !== "Unknown" &&
      rawSubType !== EmailTemplateSubType.Unknown
        ? String(rawSubType)
        : "";

    return {
      name: fileName,
      file: fileObj,
      type: enumType,
      subType: subTypeVal,
      emailTemplateSubType: subTypeVal || EmailTemplateSubType.Unknown,
      rawFile: rawF,
      // Populated by the backend validation job once it processes the file; "Under Review" until then.
      validationStatus:
        file?.ValidationStatus ?? file?.validationStatus ?? "Under Review",
      errorCount: file?.ErrorCount ?? file?.errorCount ?? 0,
      validRowCount: file?.ValidRowCount ?? file?.validRowCount ?? 0,
      totalRowCount: file?.TotalRowCount ?? file?.totalRowCount ?? 0,
      errorFileUrl: file?.ErrorFileUrl ?? file?.errorFileUrl,
    };
  };

  const buildSubmitData = (values: any) => {
    const files = Array.isArray(values.dataConversionFiles)
      ? values.dataConversionFiles.map(normalizeFile)
      : [];

    const rawFilesToUpload: File[] = [];
    const cleanFiles = files.map((f: any) => {
      if (f.rawFile instanceof File) {
        rawFilesToUpload.push(f.rawFile);
      }
      const subTypeVal =
        f.subType ||
        f.SubType ||
        f.emailTemplateSubType ||
        f.EmailTemplateSubType ||
        "";
      return {
        Name: f.name,
        Type: f.type,
        SubType: subTypeVal || null,
        EmailTemplateSubType: subTypeVal || null,
        File: f.file,
        ValidationStatus: f.validationStatus,
        ErrorCount: f.errorCount,
        ValidRowCount: f.validRowCount,
        TotalRowCount: f.totalRowCount,
        ErrorFileUrl: f.errorFileUrl,
      };
    });

    const sanitizeAsset = (val: any) => {
      if (!val) return null;
      if (typeof val === "string") {
        if (val.startsWith("blob:")) return null;
        return {
          Name: val.split("/").pop() || "file",
          Path: val,
        };
      }
      if (typeof val === "object") {
        const path = val.Path || val.path || "";
        const isBlob = path.startsWith("blob:");
        return {
          Id: val.Id || val.id,
          Name: val.Name || val.name || "",
          ContentType: val.ContentType || val.contentType,
          Length: val.Length ?? val.length,
          Path: isBlob ? "" : path,
        };
      }
      return val;
    };

    const payload = {
      ...values,
      practicePhoneNumber: Utils.normalizeUkPhoneNumberToSave(
        values.practicePhoneNumber,
      ),
      contactPersonPhone: Utils.normalizeUkPhoneNumberToSave(
        values.contactPersonPhone,
      ),
      logoUrl: sanitizeAsset(values.logoUrl),
      faviconUrl: sanitizeAsset(values.faviconUrl),
      invoiceSampleHeaderUrl: sanitizeAsset(values.invoiceSampleHeaderUrl),
      invoiceSampleFooterUrl: sanitizeAsset(values.invoiceSampleFooterUrl),
      dataConversionFiles: cleanFiles,
      currentStep,
      activeSubTab,
    };

    const hasAssetFiles = Object.keys(rawFiles).length > 0;
    if (rawFilesToUpload.length > 0 || hasAssetFiles) {
      const formData = new FormData();
      formData.append("data", JSON.stringify(payload));
      rawFilesToUpload.forEach((rf) => {
        formData.append("files", rf, rf.name);
      });
      Object.keys(rawFiles).forEach((k) => {
        const item: any = rawFiles[k];
        const actualFile = item?.originFileObj || item;
        if (
          actualFile &&
          (actualFile instanceof File ||
            actualFile instanceof Blob ||
            actualFile.size !== undefined)
        ) {
          formData.append(k, actualFile, actualFile.name || "file");
        }
      });
      return formData;
    }

    return payload;
  };

  const sortDataConversionFiles = (files: any[]) => {
    const optionsOrder = CsvTemplateOptions.map((opt) => opt.value);
    return [...(files || [])].map(normalizeFile).sort((a: any, b: any) => {
      const idxA = optionsOrder.indexOf(a.type as any);
      const idxB = optionsOrder.indexOf(b.type as any);
      return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
    });
  };

  const typeMatches = (f: any, templateType: any, subType?: any) => {
    if (
      f === null ||
      f === undefined ||
      templateType === null ||
      templateType === undefined
    )
      return false;
    const rawType = typeof f === "object" ? (f.type ?? f.Type ?? f) : f;
    const typeMatch =
      getEnumTypeValue(rawType) === getEnumTypeValue(templateType);
    if (!typeMatch) return false;

    // If we're matching an EmailTemplate and a specific subType is provided
    if (
      getEnumTypeValue(templateType) === CsvTemplateType.EmailTemplates &&
      subType !== undefined
    ) {
      const fSubType =
        typeof f === "object"
          ? (f.emailTemplateSubType ??
            f.EmailTemplateSubType ??
            f.subType ??
            f.SubType)
          : undefined;
      return fSubType === subType;
    }
    return true;
  };

  const persistdataConversionFiles = async (files: any[]) => {
    if (!routeToken) return;

    const sortedFiles = sortDataConversionFiles(files);
    const values = form.getFieldsValue(true);

    const cleanFiles = sortedFiles.map((f: any) => {
      const subTypeVal =
        f.subType ||
        f.SubType ||
        f.emailTemplateSubType ||
        f.EmailTemplateSubType ||
        "";

      const sortedData: any = {
        Name: f.name,
        Type: getEnumTypeValue(f.type),
        File: f.file?.path && !f.file.path.startsWith("blob:") ? f.file : null,
        ValidationStatus: f.validationStatus,
        ErrorCount: f.errorCount,
        ValidRowCount: f.validRowCount,
        TotalRowCount: f.totalRowCount,
        ErrorFileUrl: f.errorFileUrl,
      };
      if (subTypeVal && subTypeVal !== "Unknown") {
        sortedData.SubType = subTypeVal;
      }

      return sortedData;
    });

    const payload = {
      ...values,
      dataConversionFiles: cleanFiles,
    };

    const res = await PracticeDataRequestService.publicSubmit(
      routeToken,
      payload,
      "draft",
    );

    if (!res?.status) {
      throw new Error(res?.message || "Failed to save files");
    }

    return res;
  };

  const removeDataConversionFile = async (targetFile: any) => {
    const currentFiles = form.getFieldValue("dataConversionFiles") || [];
    const normalizedFiles = Array.isArray(currentFiles)
      ? currentFiles.map(normalizeFile)
      : [];

    const targetNorm = normalizeFile(targetFile);
    const targetId = targetNorm.file?.id;
    const targetPath = targetNorm.file?.path;
    const targetName = (targetNorm.file?.name || "").trim().toLowerCase();
    const targetType = targetNorm.type;
    const targetSubType = targetNorm.emailTemplateSubType;

    const updatedFiles = normalizedFiles.filter((f: any) => {
      const fId = f.file?.id;
      if (targetId && fId && fId === targetId) {
        return false;
      }
      const fPath = f.file?.path;
      if (targetPath && fPath && fPath === targetPath) {
        return false;
      }
      if (
        getEnumTypeValue(targetType) === CsvTemplateType.EmailTemplates &&
        typeMatches(f, targetType, targetSubType)
      ) {
        return false;
      }
      const fName = (f.name || "").trim().toLowerCase();
      if (
        getEnumTypeValue(targetType) !== CsvTemplateType.EmailTemplates &&
        fName &&
        targetName &&
        fName === targetName &&
        typeMatches(f, targetType)
      ) {
        return false;
      }
      if (
        getEnumTypeValue(targetType) !== CsvTemplateType.Other &&
        getEnumTypeValue(targetType) !== CsvTemplateType.EmailTemplates &&
        typeMatches(f, targetType)
      ) {
        return false;
      }
      return true;
    });

    const rawKey = `dataConversionFile_${targetType}${targetSubType ? `_${targetSubType}` : ""}`;
    setRawFiles((prev) => {
      const updated = { ...prev };
      delete updated[rawKey];
      return updated;
    });

    form.setFieldsValue({
      dataConversionFiles: updatedFiles,
    });

    try {
      const res = await persistdataConversionFiles(updatedFiles);
      if (res?.result) {
        form.setFieldsValue({
          dataConversionFiles: sortDataConversionFiles(
            res.result.dataConversionFiles ||
              res.result.DataConversionFiles ||
              [],
          ),
        });
      }
      message.success("File removed successfully");
    } catch (error: any) {
      message.error(error?.message || "Failed to remove file");
      fetchDataRequest();
    }
  };

  const triggerOtpFlow = () => {
    PracticeDataRequestService.getPublicRequestEmail(routeToken!)
      .then((res: any) => {
        if (res.status && res.result) {
          setOtpEmail(res.message);
          setMaskedEmail(res.result);
          setRequiresOtp(true);
        } else {
          setInvalidToken(true);
        }
        setLoading(false);
      })
      .catch(() => {
        setInvalidToken(true);
        setLoading(false);
      });
  };

  const fetchDataRequest = () => {
    setLoading(true);

    PracticeDataRequestService.getPublicRequest(routeToken!)
      .then((res: any) => {
        if (res.status && res.result) {
          const sortedFiles = sortDataConversionFiles(
            Array.isArray(res.result.dataConversionFiles)
              ? res.result.dataConversionFiles
              : [],
          );

          form.setFieldsValue({
            ...res.result,
            practicePhoneNumber: Utils.formatUkPhoneNumberForInput(
              res.result.practicePhoneNumber,
            ),
            contactPersonPhone: Utils.formatUkPhoneNumberForInput(
              res.result.contactPersonPhone,
            ),
            logoUrl: res.result.logoUrl || res.result.LogoUrl,
            faviconUrl: res.result.faviconUrl || res.result.FaviconUrl,
            invoiceSampleHeaderUrl:
              res.result.invoiceSampleHeaderUrl ||
              res.result.InvoiceSampleHeaderUrl,
            invoiceSampleFooterUrl:
              res.result.invoiceSampleFooterUrl ||
              res.result.InvoiceSampleFooterUrl,
            dataConversionFiles: sortedFiles,
            noReplyEmailOption:
              res.result.customNoReplyEmail &&
              typeof res.result.customNoReplyEmail === "string" &&
              res.result.customNoReplyEmail.trim() !== ""
                ? NoReplyEmailOption.Custom
                : NoReplyEmailOption.Default,
          });

          const reqStatus = res.result.status;
          if (
            reqStatus === PracticeDataRequestStatus.Submitted ||
            reqStatus === PracticeDataRequestStatus.Processed ||
            reqStatus === "Submitted" ||
            reqStatus === "Processed" ||
            reqStatus === 2 ||
            reqStatus === 3
          ) {
            setSubmitted(true);
            try {
              localStorage.removeItem(`pdr_step_${routeToken}`);
              localStorage.removeItem(`pdr_subtab_${routeToken}`);
              sessionStorage.removeItem(`pdr_step_${routeToken}`);
              sessionStorage.removeItem(`pdr_subtab_${routeToken}`);
            } catch {}
          } else {
            const savedStep = getInitialStep();
            const savedSubTab = getInitialSubTab(savedStep);
            const dbStep =
              res.result.currentStep &&
              (res.result.currentStep === 1 || res.result.currentStep === 2)
                ? res.result.currentStep
                : 1;
            const dbSubTab = res.result.activeSubTab;

            // Prioritize saved step from browser session/local storage, otherwise dbStep
            const targetStep = savedStep >= dbStep ? savedStep : dbStep;
            let targetSubTab =
              savedStep >= dbStep && savedSubTab
                ? savedSubTab
                : dbSubTab &&
                    SECTION_ORDER.includes(dbSubTab) &&
                    getSectionStepId(dbSubTab) === targetStep
                  ? dbSubTab
                  : getFirstSectionKey(targetStep) || "basic_info";

            if (getSectionStepId(targetSubTab) !== targetStep) {
              targetSubTab = getFirstSectionKey(targetStep) || "basic_info";
            }

            if (!res.result.practiceName && targetStep === 1) {
              setCurrentStep(1);
              setActiveSubTab("basic_info");
            } else {
              setCurrentStep(targetStep);
              setActiveSubTab(targetSubTab);
            }
          }

          setRequiresOtp(false);
          setLoading(false);
        } else {
          // If we get an unauthorized message, trigger OTP flow
          if (
            res.message === "Unauthorized access." ||
            res.status === 401 ||
            res.status === false
          ) {
            triggerOtpFlow();
          } else {
            setInvalidToken(true);
            setLoading(false);
          }
        }
      })
      .catch((err: any) => {
        if (err?.response?.status === 401) {
          triggerOtpFlow();
        } else {
          setInvalidToken(true);
          setLoading(false);
        }
      });
  };

  const handleGenerateOtp = () => {
    setOtpError("");
    setOtpLoading(true);
    PracticeDataRequestService.generateOtp(otpEmail)
      .then((res: any) => {
        if (res.status) {
          setOtpSent(true);
        } else {
          setOtpError(res.message || "Failed to generate OTP");
        }
      })
      .catch((err: any) =>
        setOtpError(err?.message || "Failed to generate OTP"),
      )
      .finally(() => setOtpLoading(false));
  };

  const handleValidateOtp = () => {
    setOtpError("");
    setOtpLoading(true);
    PracticeDataRequestService.validateOtp({ email: otpEmail, otp: otpValue })
      .then((res: any) => {
        if (res.status) {
          setRequiresOtp(false);
          fetchDataRequest();
        } else {
          setOtpError(res.message || "Invalid OTP");
        }
      })
      .catch((err: any) => setOtpError(err?.message || "Invalid OTP"))
      .finally(() => setOtpLoading(false));
  };

  useEffect(() => {
    fetchDataRequest();
    // Load dynamic email template subtypes from CRM model via API
    PracticeDataRequestService.getEmailTemplateSubTypes().then((types) => {
      if (types && types.length > 0) {
        setEmailSubTypeOptions(types);
        setNewEmailTemplateSubType(types[0].value);
      }
    });
  }, [routeToken, form]);

  // Auto-poll status every 5 seconds if any data conversion file is 'Under Review' or 'Processing'
  useEffect(() => {
    if (!routeToken || loading || requiresOtp) return;

    const interval = setInterval(() => {
      const dataFiles = form.getFieldValue("dataConversionFiles") || [];
      const hasUnderReview =
        Array.isArray(dataFiles) &&
        dataFiles.some(
          (f: any) =>
            f.validationStatus === "Under Review" ||
            f.ValidationStatus === "Under Review" ||
            f.validationStatus === "Processing" ||
            f.ValidationStatus === "Processing",
        );

      if (hasUnderReview) {
        PracticeDataRequestService.getPublicRequest(routeToken)
          .then((res: any) => {
            if (res.status && res.result) {
              const sortedFiles = sortDataConversionFiles(
                Array.isArray(res.result.dataConversionFiles)
                  ? res.result.dataConversionFiles
                  : [],
              );
              form.setFieldsValue({
                dataConversionFiles: sortedFiles,
              });
            }
          })
          .catch(() => {});
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [routeToken, loading, requiresOtp, form]);

  // Persist currentStep to sessionStorage
  useEffect(() => {
    if (routeToken) {
      try {
        sessionStorage.setItem(`pdr_step_${routeToken}`, String(currentStep));
      } catch {}
    }
  }, [currentStep, routeToken]);

  // Persist activeSubTab to sessionStorage
  useEffect(() => {
    if (routeToken && activeSubTab) {
      try {
        sessionStorage.setItem(`pdr_subtab_${routeToken}`, activeSubTab);
      } catch {}
    }
  }, [activeSubTab, routeToken]);

  const handleStepChange = async (step: number) => {
    if (step <= currentStep) {
      setCurrentStep(step);
      setActiveSubTab(getFirstSectionKey(step) || "");
      return;
    }

    try {
      const currentValues = form.getFieldsValue(true);
      if (step >= 2) {
        await form.validateFields([
          ...getRequiredFieldNames("basic_info", currentValues),
          ...getRequiredFieldNames("contact_person", currentValues),
          ...getRequiredFieldNames("branding_assets", currentValues),
        ]);
      }

      setCurrentStep(step);
      setActiveSubTab(getFirstSectionKey(step) || "");
    } catch (error: any) {
      if (error?.errorFields) {
        message.warning(
          "Please fill in all required fields before proceeding to the next step.",
        );
      }
    }
  };

  const handleSubTabClick = async (targetSubTab: string) => {
    const currentIdx = SECTION_ORDER.indexOf(activeSubTab);
    const targetIdx = SECTION_ORDER.indexOf(targetSubTab);

    if (targetIdx <= currentIdx) {
      setActiveSubTab(targetSubTab);
      return;
    }

    try {
      const currentValues = form.getFieldsValue(true);
      if (
        targetSubTab === "contact_person" ||
        targetSubTab === "branding_assets"
      ) {
        await form.validateFields(
          getRequiredFieldNames("basic_info", currentValues),
        );
      }

      if (targetSubTab === "branding_assets") {
        await form.validateFields(
          getRequiredFieldNames("contact_person", currentValues),
        );
      }

      setActiveSubTab(targetSubTab);
    } catch (error: any) {
      if (error?.errorFields) {
        message.warning(
          "Please fill in all required fields before proceeding to the next section.",
        );
      }
    }
  };

  const customUpload = async (
    options: any,
    fieldName: string | (string | number)[],
  ) => {
    const { file, onSuccess } = options;
    try {
      const fieldKey = Array.isArray(fieldName)
        ? fieldName.join(".")
        : String(fieldName);
      setRawFiles((prev) => ({ ...prev, [fieldKey]: file }));
      form.setFieldValue(fieldName, {
        Name: file.name,
        Path: URL.createObjectURL(file),
      });
      onSuccess("ok");
      message.success("File attached successfully");
    } catch (err) {
      message.error("Error attaching file");
    }
  };

  const handleUseDefaultTemplate = async (part: "header" | "footer") => {
    const fileName = part === "header" ? "AO_Header.jpg" : "AO_Footer.jpg";
    const url = `${window.location.origin}/${fileName}`;
    const field =
      part === "header" ? "invoiceSampleHeaderUrl" : "invoiceSampleFooterUrl";

    setRawFiles((prev) => {
      const updated = { ...prev };
      delete updated[field];
      return updated;
    });

    form.setFieldValue(field, {
      Name: fileName,
      Path: url,
    });
    message.success(`Default ${part} template applied!`);
  };

  const handleDownloadTemplate = (templateType: CsvTemplateType) => {
    PracticeDataRequestService.downloadCsvTemplate(templateType);
  };

  const saveDraft = async (silent = false) => {
    if (!routeToken) return;

    try {
      setSavingDraft(true);

      const values = form.getFieldsValue(true);
      const submitPayload = buildSubmitData(values);

      const res = await PracticeDataRequestService.publicSubmit(
        routeToken,
        submitPayload,
        "draft",
      );

      if (res.status) {
        setRawFiles({});
        if (res.result) {
          form.setFieldsValue({
            logoUrl: res.result.logoUrl || res.result.LogoUrl,
            faviconUrl: res.result.faviconUrl || res.result.FaviconUrl,
            invoiceSampleHeaderUrl:
              res.result.invoiceSampleHeaderUrl ||
              res.result.InvoiceSampleHeaderUrl,
            invoiceSampleFooterUrl:
              res.result.invoiceSampleFooterUrl ||
              res.result.InvoiceSampleFooterUrl,
            dataConversionFiles: sortDataConversionFiles(
              res.result.dataConversionFiles ||
                res.result.DataConversionFiles ||
                [],
            ),
          });
        }
        if (!silent) {
          message.success("Draft saved successfully.");
        }
      } else {
        if (!silent) {
          message.error(res.message || "Failed to save draft");
        }
      }
    } catch {
      if (!silent) {
        message.error("Error saving draft");
      }
    } finally {
      setSavingDraft(false);
    }
  };

  const handleNext = async () => {
    if (!routeToken) return;
    try {
      let movingToNextStep = false;
      let nextSubTab = "";

      const currentValues = form.getFieldsValue(true);
      if (currentStep === 1) {
        if (activeSubTab === "basic_info") {
          await form.validateFields(
            getRequiredFieldNames("basic_info", currentValues),
          );
          nextSubTab = "contact_person";
        } else if (activeSubTab === "contact_person") {
          await form.validateFields(
            getRequiredFieldNames("contact_person", currentValues),
          );
          nextSubTab = "branding_assets";
        } else if (activeSubTab === "branding_assets") {
          if (!form.getFieldValue("invoiceSampleHeaderUrl")) {
            await handleUseDefaultTemplate("header");
          }
          if (!form.getFieldValue("invoiceSampleFooterUrl")) {
            await handleUseDefaultTemplate("footer");
          }
          await form.validateFields([
            ...getRequiredFieldNames("basic_info", currentValues),
            ...getRequiredFieldNames("contact_person", currentValues),
            ...getRequiredFieldNames("branding_assets", currentValues),
          ]);
          movingToNextStep = true;
        }
      } else if (currentStep === 2) {
        const nextSectionKey =
          SECTION_ORDER[SECTION_ORDER.indexOf(activeSubTab) + 1];
        if (nextSectionKey && getSectionStepId(nextSectionKey) === 2) {
          nextSubTab = nextSectionKey;
        } else {
          movingToNextStep = true;
        }
      }

      const isFinalSubmit = currentStep === 2 && movingToNextStep;

      if (isFinalSubmit) {
        // 1. Validate Step 1 required fields
        try {
          await form.validateFields([
            ...getRequiredFieldNames("basic_info", currentValues),
            ...getRequiredFieldNames("contact_person", currentValues),
            ...getRequiredFieldNames("branding_assets", currentValues),
          ]);
        } catch (err: any) {
          message.warning(
            "Please complete all required fields in Practice Setup before submitting.",
          );
          handleStepChange(1);
          return;
        }

        // 2. Validate Step 2 required templates
        const currentFiles = form.getFieldValue("dataConversionFiles") || [];
        const normalizedFiles = Array.isArray(currentFiles)
          ? currentFiles.map(normalizeFile)
          : [];

        const requiredTemplates = getRequiredCsvTemplates(currentValues);
        const missingRequiredTemplate = requiredTemplates.find((reqType) => {
          const matching = normalizedFiles.find((f: any) => {
            if (!typeMatches(f, reqType)) return false;
            return Boolean(
              f.file?.path ||
              f.file?.Path ||
              f.rawFile ||
              f.fileUrl ||
              f.FileUrl,
            );
          });
          return !matching;
        });

        if (missingRequiredTemplate) {
          const templateOption = CsvTemplateOptions.find(
            (o) => o.value === missingRequiredTemplate,
          );
          const templateName = templateOption
            ? templateOption.label
            : missingRequiredTemplate;
          message.warning(
            `Please upload the required template: ${templateName} before submitting.`,
          );
          setActiveSubTab(`data_${missingRequiredTemplate}`);
          return;
        }

        // 3. Check for any uploaded files with Failed validation status
        const failedFile = normalizedFiles.find((f: any) => {
          const valStatus = f.validationStatus || f.ValidationStatus;
          return valStatus === "Failed";
        });

        if (failedFile) {
          const templateOption = CsvTemplateOptions.find(
            (o) => o.value === failedFile.type,
          );
          const templateName = templateOption
            ? templateOption.label
            : failedFile.name || failedFile.type;
          message.error(
            `The uploaded file for "${templateName}" contains validation errors. Please re-upload a valid file before submitting.`,
          );
          if (failedFile.type) {
            setActiveSubTab(`data_${failedFile.type}`);
          }
          return;
        }
      }

      setSubmitting(true);

      const values = form.getFieldsValue(true);
      const submitPayload = buildSubmitData(values);
      if (movingToNextStep && currentStep < 2) {
        submitPayload.currentStep = currentStep + 1;
        submitPayload.activeSubTab = getFirstSectionKey(currentStep + 1) || "";
      } else if (!movingToNextStep && nextSubTab) {
        submitPayload.activeSubTab = nextSubTab;
      }

      const stepType = isFinalSubmit
        ? "submit"
        : currentStep === 1
          ? "basic"
          : "data";

      const res = await PracticeDataRequestService.publicSubmit(
        routeToken,
        submitPayload,
        stepType,
      );

      if (res.status) {
        setRawFiles({});
        if (res.result) {
          form.setFieldsValue({
            logoUrl: res.result.logoUrl || res.result.LogoUrl,
            faviconUrl: res.result.faviconUrl || res.result.FaviconUrl,
            invoiceSampleHeaderUrl:
              res.result.invoiceSampleHeaderUrl ||
              res.result.InvoiceSampleHeaderUrl,
            invoiceSampleFooterUrl:
              res.result.invoiceSampleFooterUrl ||
              res.result.InvoiceSampleFooterUrl,
            dataConversionFiles: sortDataConversionFiles(
              res.result.dataConversionFiles ||
                res.result.DataConversionFiles ||
                [],
            ),
          });
        }
        if (movingToNextStep) {
          if (currentStep === 2) {
            setSubmitted(true);
            try {
              sessionStorage.removeItem(`pdr_step_${routeToken}`);
              sessionStorage.removeItem(`pdr_subtab_${routeToken}`);
            } catch {}
            message.success("Workspace setup completed successfully!");
          } else {
            handleStepChange(currentStep + 1);
          }
        } else {
          setActiveSubTab(nextSubTab);
        }
      } else {
        message.error(res.message || "Failed to save data");
      }
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }

      message.error(error?.message || "Failed to save data");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (currentStep === 1) {
      if (activeSubTab === "contact_person") {
        setActiveSubTab("basic_info");
      } else if (activeSubTab === "branding_assets") {
        setActiveSubTab("contact_person");
      }
    } else if (currentStep === 2) {
      const currentIdx = SECTION_ORDER.indexOf(activeSubTab);
      const prevSectionKey = SECTION_ORDER[currentIdx - 1];
      const prevStepId = prevSectionKey
        ? getSectionStepId(prevSectionKey)
        : undefined;

      if (prevStepId === 2) {
        setActiveSubTab(prevSectionKey);
      } else {
        setCurrentStep(1);
        setActiveSubTab("branding_assets");
      }
    }
  };

  const renderAttachmentChip = (url: any, fieldName: string) => {
    if (!url) return null;

    const urlStr = typeof url === "string" ? url : url?.Path || url?.path || "";
    let fileName =
      typeof url === "object" && (url?.Name || url?.name)
        ? url?.Name || url?.name
        : urlStr.split("/").pop() || "file";

    if (fileName.includes("?")) {
      fileName = fileName.split("?")[0];
    }
    try {
      fileName = decodeURIComponent(fileName);
    } catch {}

    const isDefault =
      fileName === "AO_Header.jpg" ||
      fileName === "AO_Footer.jpg" ||
      urlStr.includes("AO_Header.jpg") ||
      urlStr.includes("AO_Footer.jpg");

    let displayName = fileName;

    if (isDefault) {
      // Only for default invoice header and footer add prefix
      if (fieldName === "invoiceSampleHeaderUrl") {
        displayName = "Invoice Header - AO_Header.jpg";
      } else if (fieldName === "invoiceSampleFooterUrl") {
        displayName = "Invoice Footer - AO_Footer.jpg";
      }
    } else {
      // For any uploaded file: show on the basis of filename; remove id or any prefix
      if (fileName.includes("/")) {
        fileName = fileName.split("/").pop() || fileName;
      }
      const prefixMatch =
        fileName.match(/^[a-f0-9]{24}[_-](.+)$/i) ||
        fileName.match(
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}[_-](.+)$/i,
        );
      if (prefixMatch && prefixMatch[1]) {
        fileName = prefixMatch[1];
      }
      displayName = fileName;
    }

    return (
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          background: styles.chipBg,
          padding: "6px 14px",
          borderRadius: "6px",
          marginTop: "12px",
          border: "1px solid " + styles.chipBorder,
          width: "fit-content",
        }}
      >
        <PaperClipOutlined
          style={{
            marginRight: "8px",
            color: styles.chipText,
          }}
        />

        <a
          href={urlStr}
          target="_blank"
          rel="noreferrer"
          style={{
            marginRight: "12px",
            fontSize: "13px",
            color: styles.chipText,
            maxWidth: "250px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontWeight: 500,
          }}
        >
          {displayName}
        </a>

        <Button
          type="text"
          size="small"
          icon={
            <CloseOutlined
              style={{
                fontSize: "11px",
                color: "#64748b",
              }}
            />
          }
          onClick={() => {
            form.setFieldsValue({
              [fieldName]: null,
            });
            const fieldKey = String(fieldName);
            setRawFiles((prev) => {
              const updated = { ...prev };
              delete updated[fieldKey];
              return updated;
            });
          }}
          style={{
            width: "20px",
            height: "20px",
            minWidth: "20px",
            padding: 0,
            background: styles.chipCloseBg,
            borderRadius: "50%",
          }}
        />
      </div>
    );
  };

  const getPublicAssetPreviewUrl = (
    fileUrl: any,
    assetKey: string,
    defaultFallback?: string,
  ) => {
    if (!fileUrl) return defaultFallback || "";
    const rawUrl =
      typeof fileUrl === "string"
        ? fileUrl
        : fileUrl?.Path || fileUrl?.path || "";
    if (!rawUrl || rawUrl.trim() === "") return defaultFallback || "";

    if (rawUrl.startsWith("blob:") || rawUrl.startsWith("data:")) {
      return rawUrl;
    }

    if (rawUrl.includes("AO_Header.jpg") || rawUrl.includes("AO_Footer.jpg")) {
      return rawUrl.startsWith("http")
        ? rawUrl
        : `${window.location.origin}/${rawUrl.replace(/^\//, "")}`;
    }

    if (routeToken) {
      return `/api/PracticeDataRequests/PublicAsset/${routeToken}/${assetKey}?v=${encodeURIComponent(rawUrl.split("/").pop() || "")}`;
    }

    return rawUrl;
  };

  const renderAssetDragger = (
    fieldName: "logoUrl" | "faviconUrl",
    label: string,
  ) => (
    <Form.Item
      shouldUpdate={(prev, curr) => prev[fieldName] !== curr[fieldName]}
      noStyle
    >
      {({ getFieldValue }) => {
        const fileUrl = getFieldValue(fieldName);
        const hasFile = Boolean(
          fileUrl &&
          (typeof fileUrl === "string"
            ? fileUrl.trim() !== ""
            : Boolean(fileUrl?.Path || fileUrl?.path)),
        );

        if (hasFile) {
          const rawUrl =
            typeof fileUrl === "string"
              ? fileUrl
              : fileUrl?.Path || fileUrl?.path || "";
          const assetKey = fieldName === "logoUrl" ? "logo" : "favicon";
          const urlStr = getPublicAssetPreviewUrl(fileUrl, assetKey);
          let fileName =
            typeof fileUrl === "object" && (fileUrl?.Name || fileUrl?.name)
              ? fileUrl?.Name || fileUrl?.name
              : rawUrl.split("/").pop() || `${label}.png`;

          if (fileName.includes("?")) fileName = fileName.split("?")[0];
          try {
            fileName = decodeURIComponent(fileName);
          } catch {}
          if (fileName.includes("/"))
            fileName = fileName.split("/").pop() || fileName;
          const prefixMatch =
            fileName.match(/^[a-f0-9]{24}[_-](.+)$/i) ||
            fileName.match(
              /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}[_-](.+)$/i,
            );
          if (prefixMatch && prefixMatch[1]) fileName = prefixMatch[1];

          return (
            <div
              style={{
                border: "1px solid #cbd5e1",
                borderRadius: "10px",
                padding: "12px 16px",
                backgroundColor: "#ffffff",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
                height: "190px",
                minHeight: "190px",
                maxHeight: "190px",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.02)",
                boxSizing: "border-box",
                width: "100%",
              }}
            >
              <div
                style={{
                  height: "145px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  overflow: "hidden",
                }}
              >
                <Image
                  src={urlStr}
                  alt={label}
                  style={{
                    maxHeight: "135px",
                    maxWidth: "100%",
                    objectFit: "contain",
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                  preview={{
                    mask: (
                      <div
                        style={{
                          fontSize: "12px",
                          fontWeight: 500,
                          color: "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "4px",
                        }}
                      >
                        Preview {label}
                      </div>
                    ),
                  }}
                />
              </div>

              <div
                style={{
                  height: "28px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  paddingTop: "6px",
                  borderTop: "1px solid #f1f5f9",
                  boxSizing: "border-box",
                }}
              >
                <span
                  style={{
                    fontSize: "11.5px",
                    color: "#16a34a",
                    fontWeight: 600,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    maxWidth: "65%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  <CheckOutlined style={{ fontSize: "10.5px" }} /> {fileName}
                </span>

                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <Upload
                    accept=".png,.jpg,.jpeg"
                    beforeUpload={(file) => {
                      const isImage =
                        file.type === "image/jpeg" ||
                        file.type === "image/png" ||
                        file.name.toLowerCase().endsWith(".jpg") ||
                        file.name.toLowerCase().endsWith(".jpeg") ||
                        file.name.toLowerCase().endsWith(".png");
                      if (!isImage) {
                        message.error("You can only upload JPG/PNG files!");
                      }
                      return isImage || Upload.LIST_IGNORE;
                    }}
                    customRequest={(opts) => customUpload(opts, fieldName)}
                    maxCount={1}
                    showUploadList={false}
                  >
                    <Button
                      size="small"
                      type="link"
                      style={{
                        padding: 0,
                        fontSize: "11.5px",
                        fontWeight: 600,
                        color: styles.chipText,
                      }}
                    >
                      Replace
                    </Button>
                  </Upload>

                  <Button
                    size="small"
                    type="link"
                    danger
                    icon={<DeleteOutlined style={{ fontSize: "11px" }} />}
                    onClick={() => {
                      form.setFieldsValue({ [fieldName]: null });
                      setRawFiles((prev) => {
                        const updated = { ...prev };
                        delete updated[String(fieldName)];
                        return updated;
                      });
                    }}
                    style={{ padding: 0, fontSize: "11.5px" }}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            </div>
          );
        }

        return (
          <Dragger
            accept=".png,.jpg,.jpeg"
            beforeUpload={(file) => {
              const isImage =
                file.type === "image/jpeg" ||
                file.type === "image/png" ||
                file.name.toLowerCase().endsWith(".jpg") ||
                file.name.toLowerCase().endsWith(".jpeg") ||
                file.name.toLowerCase().endsWith(".png");

              if (!isImage) {
                message.error("You can only upload JPG/PNG files!");
              }

              return isImage || Upload.LIST_IGNORE;
            }}
            customRequest={(opts) => customUpload(opts, fieldName)}
            maxCount={1}
            showUploadList={false}
            style={{
              height: "190px",
              minHeight: "190px",
              maxHeight: "190px",
              background: "#fafcff",
              border: "2px dashed #cbd5e1",
              borderRadius: "10px",
              padding: "0px",
              boxSizing: "border-box",
              width: "100%",
            }}
          >
            <div
              style={{
                height: "100%",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "16px 14px",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  backgroundColor: "#f0f9ff",
                  border: "1px solid #bae6fd",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 8px auto",
                }}
              >
                <CloudUploadOutlined
                  style={{
                    fontSize: "20px",
                    color: "#0284c7",
                  }}
                />
              </div>

              <p
                style={{
                  margin: 0,
                  color: "#1e293b",
                  fontSize: "13px",
                  fontWeight: 600,
                }}
              >
                Click or drag {label} here
              </p>

              <p
                style={{
                  margin: "4px 0 0 0",
                  color: "#64748b",
                  fontSize: "11.5px",
                }}
              >
                PNG, JPG, JPEG (Max 5MB)
              </p>
            </div>
          </Dragger>
        );
      }}
    </Form.Item>
  );

  const renderTemplateSection = (
    part: "header" | "footer",
    fieldName: string,
  ) => (
    <Form.Item
      shouldUpdate={(prev, curr) => prev[fieldName] !== curr[fieldName]}
      noStyle
    >
      {({ getFieldValue }) => {
        const fileUrl = getFieldValue(fieldName);
        const hasFile = Boolean(
          fileUrl &&
          (typeof fileUrl === "string"
            ? fileUrl.trim() !== ""
            : Boolean(fileUrl?.Path || fileUrl?.path)),
        );

        const rawUrl =
          typeof fileUrl === "string"
            ? fileUrl
            : fileUrl?.Path || fileUrl?.path || "";
        const assetKey = part === "header" ? "header" : "footer";
        const urlStr = getPublicAssetPreviewUrl(fileUrl, assetKey);
        let fileName =
          typeof fileUrl === "object" && (fileUrl?.Name || fileUrl?.name)
            ? fileUrl?.Name || fileUrl?.name
            : rawUrl.split("/").pop() || `Invoice_${part}.jpg`;

        if (fileName.includes("?")) fileName = fileName.split("?")[0];
        try {
          fileName = decodeURIComponent(fileName);
        } catch {}
        if (fileName.includes("/"))
          fileName = fileName.split("/").pop() || fileName;
        const prefixMatch =
          fileName.match(/^[a-f0-9]{24}[_-](.+)$/i) ||
          fileName.match(
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}[_-](.+)$/i,
          );
        if (prefixMatch && prefixMatch[1]) fileName = prefixMatch[1];

        if (hasFile) {
          return (
            <div
              style={{
                border: "1px solid #cbd5e1",
                borderRadius: "10px",
                padding: "12px 16px",
                backgroundColor: "#ffffff",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
                height: "205px",
                minHeight: "205px",
                maxHeight: "205px",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.02)",
                boxSizing: "border-box",
                width: "100%",
              }}
            >
              <div
                style={{
                  height: "145px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  overflow: "hidden",
                }}
              >
                <Image
                  src={urlStr}
                  alt={`Invoice ${part}`}
                  style={{
                    maxHeight: "135px",
                    maxWidth: "100%",
                    objectFit: "contain",
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                  preview={{
                    mask: (
                      <div
                        style={{
                          fontSize: "12px",
                          fontWeight: 500,
                          color: "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "4px",
                        }}
                      >
                        Preview Invoice {part}
                      </div>
                    ),
                  }}
                />
              </div>

              <div
                style={{
                  height: "28px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  paddingTop: "6px",
                  borderTop: "1px solid #f1f5f9",
                  boxSizing: "border-box",
                }}
              >
                <span
                  style={{
                    fontSize: "11.5px",
                    color: "#16a34a",
                    fontWeight: 600,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    maxWidth: "65%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  <CheckOutlined style={{ fontSize: "10.5px" }} /> {fileName}
                </span>

                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <Upload
                    accept=".png,.jpg,.jpeg"
                    beforeUpload={(file) => {
                      const isImage =
                        file.type === "image/jpeg" ||
                        file.type === "image/png" ||
                        file.name.toLowerCase().endsWith(".jpg") ||
                        file.name.toLowerCase().endsWith(".jpeg") ||
                        file.name.toLowerCase().endsWith(".png");
                      if (!isImage) {
                        message.error("You can only upload JPG/PNG files!");
                      }
                      return isImage || Upload.LIST_IGNORE;
                    }}
                    customRequest={(opts) => customUpload(opts, fieldName)}
                    maxCount={1}
                    showUploadList={false}
                  >
                    <Button
                      size="small"
                      type="link"
                      style={{
                        padding: 0,
                        fontSize: "11.5px",
                        fontWeight: 600,
                        color: styles.chipText,
                      }}
                    >
                      Replace
                    </Button>
                  </Upload>

                  <Button
                    size="small"
                    type="link"
                    danger
                    icon={<DeleteOutlined style={{ fontSize: "11px" }} />}
                    onClick={() => {
                      form.setFieldsValue({ [fieldName]: null });
                      setRawFiles((prev) => {
                        const updated = { ...prev };
                        delete updated[String(fieldName)];
                        return updated;
                      });
                    }}
                    style={{ padding: 0, fontSize: "11.5px" }}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            </div>
          );
        }

        return (
          <Dragger
            accept=".png,.jpg,.jpeg"
            beforeUpload={(file) => {
              const isImage =
                file.type === "image/jpeg" ||
                file.type === "image/png" ||
                file.name.toLowerCase().endsWith(".jpg") ||
                file.name.toLowerCase().endsWith(".jpeg") ||
                file.name.toLowerCase().endsWith(".png");

              if (!isImage) {
                message.error("You can only upload JPG/PNG files!");
              }

              return isImage || Upload.LIST_IGNORE;
            }}
            customRequest={(opts) => customUpload(opts, fieldName)}
            maxCount={1}
            showUploadList={false}
            style={{
              height: "205px",
              minHeight: "205px",
              maxHeight: "205px",
              background: "#fafcff",
              border: "2px dashed #cbd5e1",
              borderRadius: "10px",
              padding: "0px",
              boxSizing: "border-box",
              width: "100%",
            }}
          >
            <div
              style={{
                height: "100%",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "16px 14px",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  backgroundColor: "#f0f9ff",
                  border: "1px solid #bae6fd",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 8px auto",
                }}
              >
                <CloudUploadOutlined
                  style={{
                    fontSize: "20px",
                    color: "#0284c7",
                  }}
                />
              </div>

              <p
                style={{
                  margin: 0,
                  color: "#1e293b",
                  fontSize: "13px",
                  fontWeight: 600,
                }}
              >
                Click or drag Invoice {part} image here
              </p>

              <p
                style={{
                  margin: "4px 0 0 0",
                  color: "#64748b",
                  fontSize: "11.5px",
                }}
              >
                PNG, JPG, JPEG (Max 5MB)
              </p>
            </div>
          </Dragger>
        );
      }}
    </Form.Item>
  );

  const renderUploadedFileReviewCard = (fileItem: any) => {
    if (!fileItem) return null;
    const fileUrl = fileItem.file?.path || fileItem.File?.Path || "";
    const fileId =
      fileItem.file?.id ||
      fileItem.File?.Id ||
      fileItem.file?.name ||
      fileItem.File?.Name ||
      fileItem.name ||
      fileItem.Name;
    const downloadUrl =
      routeToken && fileId
        ? `/api/PracticeDataRequests/PublicFile/${routeToken}/${encodeURIComponent(fileId)}`
        : fileUrl;
    const displayName =
      fileItem.name ||
      fileItem.Name ||
      fileItem.file?.name ||
      fileItem.File?.Name ||
      "Uploaded File";
    const status =
      fileItem.validationStatus || fileItem.ValidationStatus || "Under Review";
    const totalRecords =
      fileItem.totalRowCount ??
      fileItem.TotalRowCount ??
      fileItem.totalRecords ??
      fileItem.TotalRecords;
    const validRecords = fileItem.validRowCount ?? fileItem.ValidRowCount;
    const errorCount = fileItem.errorCount ?? fileItem.ErrorCount ?? 0;
    const errorDetailsJson =
      fileItem.errorDetailsJson || fileItem.ErrorDetailsJson;
    const errorFileUrl = fileItem.errorFileUrl || fileItem.ErrorFileUrl;

    let parsedErrors: any[] = [];
    if (errorDetailsJson) {
      try {
        parsedErrors =
          typeof errorDetailsJson === "string"
            ? JSON.parse(errorDetailsJson)
            : errorDetailsJson;
      } catch {}
    }

    const isPassed = status === "Passed" || status === "Completed";
    const isFailed = status === "Failed";
    const isUnderReview = !isPassed && !isFailed;

    const statusBadge = isPassed ? (
      <Tag
        style={{
          backgroundColor: "#f0f9ff",
          color: "#0284c7",
          borderColor: "#e2e8f0",
          fontSize: "12px",
          fontWeight: 500,
          borderRadius: "4px",
          padding: "2px 8px",
        }}
        icon={<CheckCircleFilled style={{ color: "#0284c7" }} />}
      >
        Verified &amp; Ready
      </Tag>
    ) : isFailed ? (
      <Tag
        style={{
          backgroundColor: "#fef2f2",
          color: "#dc2626",
          border: "1px solid #fecaca",
          fontWeight: 500,
          borderRadius: "4px",
          padding: "2px 8px",
        }}
        icon={<CloseCircleFilled style={{ color: "#dc2626" }} />}
      >
        Needs Attention ({errorCount} {errorCount === 1 ? "item" : "items"})
      </Tag>
    ) : (
      <Tag color="processing" icon={<SyncOutlined spin />}>
        Under Review
      </Tag>
    );

    return (
      <div
        style={{
          border: "1px solid #e2e8f0",
          borderRadius: "10px",
          padding: "14px 18px",
          backgroundColor: "#ffffff",
          boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        {/* Top File Bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          <PaperClipOutlined style={{ fontSize: "16px", color: "#0284c7" }} />
          <a
            href={fileUrl || "#"}
            target="_blank"
            rel="noreferrer"
            style={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#0284c7",
              maxWidth: "380px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {displayName}
          </a>
          {statusBadge}
        </div>

        {/* Under Review Message */}
        {isUnderReview && (
          <div
            style={{
              fontSize: "12.5px",
              color: "#0369a1",
              lineHeight: "1.5",
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              marginTop: "2px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <SyncOutlined spin style={{ fontSize: "13px" }} />
              <span>Your file was uploaded successfully!</span>
            </div>
            <div style={{ color: "#64748b", fontSize: "12px" }}>
              Our system is currently checking your information.
              <br /> We will update you once the review is complete.
            </div>
          </div>
        )}

        {/* Verified Message with Line Breaks */}
        {isPassed && (
          <div
            style={{
              fontSize: "13px",
              color: "#334155",
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              marginTop: "2px",
            }}
          >
            <div>
              Everything looks great! <br />
              All records were checked and verified successfully.
            </div>
            {totalRecords !== undefined && (
              <div
                style={{
                  fontWeight: 600,
                  color: "#0284c7",
                  fontSize: "12.5px",
                }}
              >
                ({totalRecords} Total Records Verified)
              </div>
            )}
          </div>
        )}

        {/* Failed / Needs Attention Breakdown */}
        {isFailed && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              marginTop: "2px",
            }}
          >
            <div
              style={{
                fontSize: "12.5px",
                color: "#475569",
                fontWeight: 500,
              }}
            >
              We found a few items in your file that need to be corrected before
              we can process it.
            </div>

            {(totalRecords !== undefined ||
              validRecords !== undefined ||
              errorCount > 0) && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  fontSize: "12px",
                  color: "#475569",
                  backgroundColor: "#f8fafc",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  border: "1px solid #e2e8f0",
                  flexWrap: "wrap",
                }}
              >
                {totalRecords !== undefined && (
                  <span>
                    <strong>Total:</strong> {totalRecords}
                  </span>
                )}
                {validRecords !== undefined && (
                  <span style={{ color: "#16a34a" }}>
                    <strong>Valid:</strong> {validRecords}
                  </span>
                )}
                {errorCount > 0 && (
                  <span style={{ color: "#dc2626" }}>
                    <strong>Errors:</strong> {errorCount}
                  </span>
                )}
              </div>
            )}

            {Array.isArray(parsedErrors) && parsedErrors.length > 0 && (
              <div
                style={{
                  backgroundColor: "#f8fafc",
                  borderRadius: "6px",
                  border: "1px solid #e2e8f0",
                  padding: "10px 12px",
                  maxHeight: "150px",
                  overflowY: "auto",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#475569",
                    marginBottom: "6px",
                  }}
                >
                  Items Needing Attention:
                </div>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: "18px",
                    fontSize: "12px",
                    color: "#334155",
                  }}
                >
                  {parsedErrors.slice(0, 5).map((errItem: any, idx: number) => (
                    <li key={idx} style={{ marginBottom: "4px" }}>
                      {typeof errItem === "string"
                        ? errItem
                        : `Row ${errItem.row || errItem.line || idx + 1}: ${
                            errItem.message ||
                            errItem.error ||
                            errItem.column ||
                            "Invalid information"
                          }`}
                    </li>
                  ))}
                  {parsedErrors.length > 5 && (
                    <li style={{ fontStyle: "italic", color: "#64748b" }}>
                      And {parsedErrors.length - 5} more items...
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Bottom Action Row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: "6px",
            paddingTop: "10px",
            borderTop: "1px dashed #e2e8f0",
            flexWrap: "wrap",
            gap: "8px",
          }}
        >
          <Space size="small" wrap>
            {downloadUrl && (
              <Button
                size="small"
                icon={<DownloadOutlined />}
                href={downloadUrl}
                target="_blank"
                download={displayName}
                style={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#0284c7",
                  borderColor: "#bae6fd",
                  background: "#f0f9ff",
                  borderRadius: "6px",
                }}
              >
                Download Uploaded File
              </Button>
            )}

            {(errorFileUrl ||
              (Array.isArray(parsedErrors) && parsedErrors.length > 0)) && (
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => {
                  if (errorFileUrl) {
                    const link = document.createElement("a");
                    link.href = errorFileUrl;
                    link.download = `${displayName}_IssueReport.csv`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  } else if (
                    Array.isArray(parsedErrors) &&
                    parsedErrors.length > 0
                  ) {
                    let csvContent =
                      "Line Number,Column Name,Input Value,Error Description\n";
                    parsedErrors.forEach((err: any, idx: number) => {
                      const rowNum = err.row || err.line || idx + 1;
                      const colName = (err.column || err.colName || "").replace(
                        /"/g,
                        '""',
                      );
                      const val = (err.value || "").replace(/"/g, '""');
                      const msg = (err.message || err.error || "").replace(
                        /"/g,
                        '""',
                      );
                      csvContent += `${rowNum},"${colName}","${val}","${msg}"\n`;
                    });
                    const blob = new Blob([csvContent], {
                      type: "text/csv;charset=utf-8;",
                    });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `${displayName}_IssueReport.csv`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  }
                }}
                style={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#dc2626",
                  borderColor: "#fecaca",
                  background: "#fef2f2",
                  borderRadius: "6px",
                }}
              >
                Download Issue Report (.CSV)
              </Button>
            )}
          </Space>

          <Button
            size="small"
            type="link"
            danger
            icon={<CloseOutlined style={{ fontSize: "11.5px" }} />}
            onClick={() => removeDataConversionFile(fileItem)}
            style={{ fontSize: "12px", fontWeight: 600 }}
          >
            Cancel &amp; Re-upload
          </Button>
        </div>
      </div>
    );
  };

  const renderDataImportFileField = (config: IFieldConfig) => {
    const templateType = config.fileTemplateType!;
    const isOther = templateType === CsvTemplateType.Other;
    const isEmailTemplate = templateType === CsvTemplateType.EmailTemplates;

    const handleUpload = async (opts: any) => {
      const { file, onSuccess, onError } = opts;

      if (isOther && !otherFileName.trim()) {
        message.error("Please enter a description before uploading.");
        if (onError) onError(new Error("Missing description"));
        return;
      }

      try {
        const currentFiles = form.getFieldValue("dataConversionFiles") || [];
        const normalizedFiles = Array.isArray(currentFiles)
          ? currentFiles.map(normalizeFile)
          : [];

        const name = isOther ? otherFileName.trim() : (file as File).name;
        const isEmailTemplate = templateType === CsvTemplateType.EmailTemplates;
        const isWordDoc =
          templateType === CsvTemplateType.EmailTemplates ||
          templateType === CsvTemplateType.ESignatureContent ||
          templateType === CsvTemplateType.LoeContent;

        const subTypeVal = isEmailTemplate
          ? String(newEmailTemplateSubType)
          : isOther && otherFileName.trim()
            ? otherFileName.trim()
            : "";

        // Set Under Review status in memory while file is uploaded to C# backend
        const filesUnderReview = [
          ...normalizedFiles.filter(
            (f: any) =>
              !typeMatches(
                f,
                templateType,
                isEmailTemplate ? newEmailTemplateSubType : undefined,
              ),
          ),
          {
            name,
            type: templateType,
            subType: subTypeVal,
            emailTemplateSubType: isEmailTemplate ? subTypeVal : undefined,
            file: {
              Name: (file as File).name,
              Path: URL.createObjectURL(file as File),
            },
            rawFile: file as File,
            validationStatus: "Under Review",
          },
        ];
        setRawFiles((prev) => ({
          ...prev,
          [`dataConversionFile_${templateType}${isEmailTemplate ? `_${newEmailTemplateSubType}` : ""}`]:
            file,
        }));

        form.setFieldsValue({ dataConversionFiles: filesUnderReview });

        if (onSuccess) onSuccess("ok");

        // Directly transmit file payload to C# backend API silently for server-side column validation
        setTimeout(() => {
          saveDraft(true);
        }, 100);
      } catch (error: any) {
        message.error(error?.message || "Error uploading file");
        if (onError) onError(error);
      }
    };

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {/* Top Action Row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontSize: "13px",
              color: styles.textSecondary,
              fontWeight: 400,
            }}
          >
            Import data for {config.label.toLowerCase()} using the official
            template.
          </span>

          {!isOther && (
            <Button
              size="middle"
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadTemplate(templateType)}
              style={{
                borderRadius: "6px",
                fontSize: "13px",
                fontWeight: 600,
                color: styles.chipText,
                borderColor: styles.chipBorder,
                background: styles.chipBg,
              }}
            >
              Download Template
            </Button>
          )}
        </div>

        {/* SCIM info banner for Users Import */}
        {templateType === CsvTemplateType.Users &&
          (form.getFieldValue("userCreationMethod") ===
            UserCreationMethod.CreateViaSCIM ||
            form.getFieldValue("userCreationMethod") === 2 ||
            form.getFieldValue("userCreationMethod") === "CreateViaSCIM") && (
            <div
              style={{
                padding: "10px 14px",
                borderRadius: "8px",
                backgroundColor: "#f0f9ff",
                border: "1px solid #bae6fd",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                fontSize: "13px",
                color: "#0369a1",
              }}
            >
              <InfoCircleOutlined
                style={{ fontSize: "16px", color: "#0284c7" }}
              />
              <span>
                <strong>SCIM User Provisioning Enabled:</strong> User creation
                via SCIM was selected in Practice Setup. Uploading a Users
                spreadsheet is optional.
              </span>
            </div>
          )}

        {/* Input for Custom "Other" Imports */}
        {isOther && (
          <Input
            placeholder="Enter description (e.g. Historical Trial Balance 2024)"
            size="middle"
            value={otherFileName}
            onChange={(e) => setOtherFileName(e.target.value)}
            style={{ borderRadius: "6px" }}
          />
        )}

        {/* Single Main Content Block: Uploaded File Card OR Dropzone */}
        <Form.Item
          shouldUpdate={(prev, curr) =>
            prev.dataConversionFiles !== curr.dataConversionFiles
          }
          noStyle
        >
          {({ getFieldValue }) => {
            const files = getFieldValue("dataConversionFiles") || [];
            const isEmailTemplate =
              templateType === CsvTemplateType.EmailTemplates;

            // For email templates, allow multiple files (one per subType). Filter for all email templates.
            const uploadedFiles = files.filter((f: any) =>
              typeMatches(f, templateType),
            );
            const uploaded =
              !isEmailTemplate && uploadedFiles.length > 0
                ? uploadedFiles[0]
                : null;

            // If Email Templates: Render 2-column layout with vertical side menu on left and upload/review card on right
            if (isEmailTemplate) {
              const selectedOption =
                emailSubTypeOptions.find(
                  (o) => o.value === newEmailTemplateSubType,
                ) || emailSubTypeOptions[0];

              const currentUploadedForSelected = uploadedFiles.find(
                (f: any) =>
                  (f.emailTemplateSubType ?? f.EmailTemplateSubType) ===
                  selectedOption.value,
              );

              const filteredOptions = emailSubTypeOptions.filter((opt) =>
                opt.label
                  .toLowerCase()
                  .includes(emailSearchText.toLowerCase().trim()),
              );

              const selectedStatus =
                currentUploadedForSelected?.validationStatus ||
                currentUploadedForSelected?.ValidationStatus ||
                "";
              const isSelectedPassed =
                selectedStatus === "Passed" || selectedStatus === "Completed";
              const isSelectedFailed = selectedStatus === "Failed";

              return (
                <div
                  style={{
                    display: "flex",
                    gap: "20px",
                    alignItems: "stretch",
                    minHeight: "480px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "10px",
                    padding: "16px",
                    backgroundColor: "#fafcff",
                  }}
                >
                  {/* Left: Inner Vertical Side Menu to choose uploads */}
                  <div
                    style={{
                      width: "270px",
                      flexShrink: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                      borderRight: "1px solid #e2e8f0",
                      paddingRight: "16px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: styles.textTitle,
                        }}
                      >
                        Email Templates
                      </span>
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 600,
                          padding: "2px 8px",
                          borderRadius: "10px",
                          backgroundColor: "#f1f5f9",
                          color: styles.textSecondary,
                        }}
                      >
                        {uploadedFiles.length} of {emailSubTypeOptions.length}{" "}
                        Uploaded
                      </span>
                    </div>

                    <Input
                      size="small"
                      prefix={
                        <SearchOutlined
                          style={{
                            color: styles.textSecondary,
                            fontSize: "12px",
                          }}
                        />
                      }
                      placeholder="Search templates..."
                      value={emailSearchText}
                      onChange={(e) => setEmailSearchText(e.target.value)}
                      allowClear
                      style={{ borderRadius: "6px", fontSize: "12px" }}
                    />

                    <div
                      className="custom-card-scroll"
                      style={{
                        flex: 1,
                        maxHeight: "380px",
                        overflowY: "auto",
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                        paddingRight: "4px",
                      }}
                    >
                      {filteredOptions.map((opt) => {
                        const isSelected =
                          opt.value === newEmailTemplateSubType;
                        const fileForOpt = uploadedFiles.find(
                          (f: any) =>
                            (f.emailTemplateSubType ??
                              f.EmailTemplateSubType) === opt.value,
                        );
                        const hasFile = Boolean(fileForOpt);
                        const st =
                          fileForOpt?.validationStatus ||
                          fileForOpt?.ValidationStatus ||
                          "";
                        const isPassed = st === "Passed" || st === "Completed";
                        const isFailed = st === "Failed";
                        const isUnderReview = hasFile && !isPassed && !isFailed;

                        return (
                          <div
                            key={opt.value}
                            onClick={() =>
                              setNewEmailTemplateSubType(opt.value)
                            }
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "8px 10px",
                              borderRadius: "6px",
                              fontSize: "12.5px",
                              fontWeight: isSelected ? 600 : 400,
                              color: isSelected
                                ? styles.chipText
                                : styles.textPrimary,
                              backgroundColor: isSelected
                                ? styles.tabActiveBg || "#e0f2fe"
                                : "transparent",
                              cursor: "pointer",
                              transition: "all 0.15s ease",
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.backgroundColor =
                                  "#f1f5f9";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.backgroundColor =
                                  "transparent";
                              }
                            }}
                          >
                            <span
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "195px",
                              }}
                              title={opt.label}
                            >
                              {opt.label}
                            </span>

                            {isPassed && (
                              <CheckCircleFilled
                                style={{ color: "#16a34a", fontSize: "13px" }}
                                title="Verified & Ready"
                              />
                            )}
                            {isFailed && (
                              <CloseCircleFilled
                                style={{ color: "#dc2626", fontSize: "13px" }}
                                title="Needs Attention"
                              />
                            )}
                            {isUnderReview && (
                              <SyncOutlined
                                spin
                                style={{ color: "#0284c7", fontSize: "12px" }}
                                title="Under Review"
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Right: Active Template Upload / Review Area */}
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: "14px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        paddingBottom: "10px",
                        borderBottom: "1px solid #e2e8f0",
                        flexWrap: "wrap",
                        gap: "8px",
                      }}
                    >
                      <div>
                        <h4
                          style={{
                            margin: 0,
                            fontSize: "15px",
                            fontWeight: 600,
                            color: styles.textTitle,
                          }}
                        >
                          {selectedOption.label}
                        </h4>
                        <span
                          style={{
                            fontSize: "12px",
                            color: styles.textSecondary,
                          }}
                        >
                          {currentUploadedForSelected
                            ? "File uploaded for this sub-type. You can download or re-upload below."
                            : `Upload Word document template for ${selectedOption.label}.`}
                        </span>
                      </div>

                      {currentUploadedForSelected ? (
                        isSelectedPassed ? (
                          <Tag
                            style={{
                              backgroundColor: "#f0f9ff",
                              color: "#0284c7",
                              borderColor: "#e2e8f0",
                              fontSize: "12px",
                              fontWeight: 500,
                              borderRadius: "4px",
                            }}
                            icon={
                              <CheckCircleFilled style={{ color: "#0284c7" }} />
                            }
                          >
                            Verified &amp; Ready
                          </Tag>
                        ) : isSelectedFailed ? (
                          <Tag
                            style={{
                              backgroundColor: "#fef2f2",
                              color: "#dc2626",
                              border: "1px solid #fecaca",
                              fontWeight: 500,
                              borderRadius: "4px",
                            }}
                            icon={
                              <CloseCircleFilled style={{ color: "#dc2626" }} />
                            }
                          >
                            Needs Attention
                          </Tag>
                        ) : (
                          <Tag color="processing" icon={<SyncOutlined spin />}>
                            Under Review
                          </Tag>
                        )
                      ) : (
                        <Tag
                          style={{
                            backgroundColor: "#f1f5f9",
                            color: "#64748b",
                            border: "1px solid #e2e8f0",
                            borderRadius: "4px",
                            fontSize: "11.5px",
                            fontWeight: 500,
                          }}
                        >
                          Not Uploaded
                        </Tag>
                      )}
                    </div>

                    {currentUploadedForSelected ? (
                      renderUploadedFileReviewCard(currentUploadedForSelected)
                    ) : (
                      <Dragger
                        accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        maxCount={1}
                        showUploadList={false}
                        beforeUpload={(file) => {
                          const isDoc =
                            file.name.toLowerCase().endsWith(".docx") ||
                            file.name.toLowerCase().endsWith(".doc") ||
                            file.type.includes("word") ||
                            file.type.includes("document");

                          if (!isDoc) {
                            message.error(
                              "You can only upload Word document files (.docx, .doc)!",
                            );
                          }

                          return isDoc || Upload.LIST_IGNORE;
                        }}
                        customRequest={handleUpload}
                        style={{
                          background: "#ffffff",
                          border: "2px dashed #cbd5e1",
                          borderRadius: "10px",
                          minHeight: "220px",
                          padding: "0px",
                          boxSizing: "border-box",
                          width: "100%",
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "36px 14px",
                            boxSizing: "border-box",
                          }}
                        >
                          <div
                            style={{
                              width: "44px",
                              height: "44px",
                              borderRadius: "50%",
                              backgroundColor: "#f0f9ff",
                              border: "1px solid #bae6fd",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              margin: "0 auto 10px auto",
                            }}
                          >
                            <CloudUploadOutlined
                              style={{
                                fontSize: "22px",
                                color: "#0284c7",
                              }}
                            />
                          </div>

                          <p
                            style={{
                              margin: 0,
                              color: "#1e293b",
                              fontSize: "13.5px",
                              fontWeight: 600,
                            }}
                          >
                            Drag &amp; drop Word template for{" "}
                            {selectedOption.label} here
                          </p>

                          <p
                            style={{
                              margin: "4px 0 0 0",
                              color: "#64748b",
                              fontSize: "12px",
                            }}
                          >
                            or{" "}
                            <span style={{ color: "#0284c7", fontWeight: 600 }}>
                              click to browse
                            </span>{" "}
                            (.DOCX or .DOC)
                          </p>
                        </div>
                      </Dragger>
                    )}
                  </div>
                </div>
              );
            }

            const isWordDoc =
              templateType === CsvTemplateType.ESignatureContent ||
              templateType === CsvTemplateType.LoeContent;

            const acceptType = isWordDoc
              ? ".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              : ".csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv";

            // Standard (non-EmailTemplate) view
            return (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                {uploaded ? (
                  renderUploadedFileReviewCard(uploaded)
                ) : (
                  <Dragger
                    accept={acceptType}
                    maxCount={1}
                    showUploadList={false}
                    beforeUpload={(file) => {
                      if (isWordDoc) {
                        const isDoc =
                          file.name.toLowerCase().endsWith(".docx") ||
                          file.name.toLowerCase().endsWith(".doc") ||
                          file.type.includes("word") ||
                          file.type.includes("document");

                        if (!isDoc) {
                          message.error(
                            "You can only upload Word document files (.docx, .doc)!",
                          );
                        }
                        return isDoc || Upload.LIST_IGNORE;
                      } else {
                        const isCsv =
                          file.type === "text/csv" ||
                          file.name.toLowerCase().endsWith(".csv") ||
                          file.name.toLowerCase().endsWith(".xlsx");

                        if (!isCsv) {
                          message.error(
                            "You can only upload CSV or Excel files!",
                          );
                        }
                        return isCsv || Upload.LIST_IGNORE;
                      }
                    }}
                    customRequest={handleUpload}
                    style={{
                      background: "#fafcff",
                      border: "2px dashed #cbd5e1",
                      borderRadius: "10px",
                      height: "155px",
                      minHeight: "155px",
                      maxHeight: "155px",
                      padding: "0px",
                      boxSizing: "border-box",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "16px 14px",
                        boxSizing: "border-box",
                      }}
                    >
                      <div
                        style={{
                          width: "42px",
                          height: "42px",
                          borderRadius: "50%",
                          backgroundColor: "#f0f9ff",
                          border: "1px solid #bae6fd",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "0 auto 10px auto",
                        }}
                      >
                        <CloudUploadOutlined
                          style={{
                            fontSize: "22px",
                            color: "#0284c7",
                          }}
                        />
                      </div>

                      <p
                        style={{
                          margin: 0,
                          color: "#1e293b",
                          fontSize: "13.5px",
                          fontWeight: 600,
                        }}
                      >
                        Drag &amp; drop your file here
                      </p>

                      <p
                        style={{
                          margin: "4px 0 0 0",
                          color: "#64748b",
                          fontSize: "12px",
                        }}
                      >
                        or{" "}
                        <span style={{ color: "#0284c7", fontWeight: 600 }}>
                          click to browse
                        </span>{" "}
                        {isWordDoc ? "(.DOCX or .DOC)" : "(.CSV or .XLSX)"}
                      </p>
                    </div>
                  </Dragger>
                )}
              </div>
            );
          }}
        </Form.Item>
      </div>
    );
  };

  const renderLeftMenuItem = (
    key: string,
    label: string,
    statusIndicator?: React.ReactNode | boolean | number,
    isRequired?: boolean,
  ) => {
    const isActive = activeSubTab === key;
    const isHovered = hoveredTab === key;

    return (
      <div
        key={key}
        onClick={() => handleSubTabClick(key)}
        onMouseEnter={() => setHoveredTab(key)}
        onMouseLeave={() => setHoveredTab(null)}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "7px 12px",
          borderRadius: "8px",
          fontSize: "13px",
          fontWeight: isActive ? 600 : 500,
          color: isActive ? styles.chipText : styles.textPrimary,
          backgroundColor: isActive
            ? styles.tabActiveBg
            : isHovered
              ? styles.tabActiveBg
              : "transparent",
          cursor: "pointer",
          transition: "all 0.15s ease-in-out",
        }}
      >
        <span>
          {label}
          {isRequired && (
            <span
              style={{
                color: styles.errorColor || "#ef4444",
                marginLeft: "4px",
              }}
            >
              *
            </span>
          )}
        </span>

        {statusIndicator && (
          <span
            style={{
              fontSize: "12px",
              display: "flex",
              alignItems: "center",
            }}
          >
            {typeof statusIndicator === "boolean" ? (
              <CheckOutlined
                style={{
                  color: isActive ? styles.chipText : styles.successColor,
                }}
              />
            ) : typeof statusIndicator === "number" ? (
              statusIndicator
            ) : (
              statusIndicator
            )}
          </span>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: styles.secondaryBg,
        }}
      >
        <Spin indicator={<LoadingOutlined />} />
      </div>
    );
  }

  if (requiresOtp) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: styles.pageBg,
          padding: "24px",
          fontFamily:
            "'Google Sans Flex', 'Google Sans', 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, sans-serif",
        }}
      >
        <div
          style={{
            padding: "40px 36px",
            background: styles.cardBg,
            borderRadius: "20px",
            boxShadow: "0 12px 36px rgba(15, 23, 42, 0.06)",
            maxWidth: "430px",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <div style={{ textAlign: "center", marginBottom: "20px" }}>
            <img
              src={logoImg}
              alt="acting office"
              style={{
                height: "44px",
                objectFit: "contain",
              }}
            />
          </div>

          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: "52px",
                height: "52px",
                borderRadius: "50%",
                backgroundColor: styles.tabActiveBg || "#F0F9FF",
                border: "1px solid #BAE6FD",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px auto",
              }}
            >
              <LockOutlined
                style={{
                  fontSize: "22px",
                  color: styles.chipText,
                }}
              />
            </div>

            <h2
              style={{
                fontSize: "20px",
                fontWeight: 600,
                color: styles.textTitle,
                margin: "0 0 6px 0",
              }}
            >
              Secure Workspace
            </h2>

            <Text
              style={{
                fontSize: "13.5px",
                color: styles.textSecondary,
              }}
            >
              For your security, we need to verify your identity.
            </Text>
          </div>

          <div style={{ marginTop: "24px" }}>
            {!otpSent ? (
              <div>
                <div
                  style={{
                    backgroundColor: styles.secondaryBg,
                    borderRadius: "12px",
                    padding: "14px 18px",
                    border: "1px solid " + styles.borderLightColor,
                    marginBottom: "20px",
                    textAlign: "center",
                  }}
                >
                  <Text
                    style={{
                      fontSize: "12.5px",
                      color: styles.textSecondary,
                      display: "block",
                      marginBottom: "4px",
                    }}
                  >
                    We will send a One Time Password (OTP) to:
                  </Text>
                  <Text
                    style={{
                      fontSize: "14.5px",
                      fontWeight: 600,
                      color: styles.textPrimary,
                      wordBreak: "break-all",
                    }}
                  >
                    {maskedEmail}
                  </Text>
                </div>

                <Button
                  type="primary"
                  size="large"
                  onClick={handleGenerateOtp}
                  loading={otpLoading}
                  style={{
                    width: "100%",
                    height: "46px",
                    borderRadius: "10px",
                    background: styles.btnPrimaryBg,
                    fontSize: "14.5px",
                    fontWeight: 600,
                    border: "none",
                    boxShadow: "0 4px 14px rgba(2, 132, 199, 0.2)",
                  }}
                >
                  Send OTP
                </Button>
              </div>
            ) : (
              <div>
                <div
                  style={{
                    backgroundColor: styles.secondaryBg,
                    borderRadius: "12px",
                    padding: "12px 16px",
                    border: "1px solid " + styles.borderLightColor,
                    marginBottom: "18px",
                    textAlign: "center",
                  }}
                >
                  <Text
                    style={{
                      fontSize: "13px",
                      color: styles.textSecondary,
                      display: "block",
                    }}
                  >
                    Enter the 6-digit OTP sent to{" "}
                    <strong style={{ color: styles.textPrimary }}>
                      {maskedEmail}
                    </strong>
                  </Text>
                </div>

                <Input
                  size="large"
                  placeholder="------"
                  value={otpValue}
                  onChange={(e) => setOtpValue(e.target.value)}
                  style={{
                    height: "48px",
                    marginBottom: "18px",
                    textAlign: "center",
                    letterSpacing: "10px",
                    fontSize: "22px",
                    fontWeight: 600,
                    borderRadius: "10px",
                  }}
                  maxLength={6}
                />

                <Button
                  type="primary"
                  size="large"
                  onClick={handleValidateOtp}
                  loading={otpLoading}
                  disabled={otpValue.length < 4}
                  style={{
                    width: "100%",
                    height: "46px",
                    borderRadius: "10px",
                    background: styles.btnPrimaryBg,
                    fontSize: "14.5px",
                    fontWeight: 600,
                    border: "none",
                    boxShadow: "0 4px 14px rgba(2, 132, 199, 0.2)",
                  }}
                >
                  Verify Securely
                </Button>

                <div
                  style={{
                    textAlign: "center",
                    marginTop: "16px",
                  }}
                >
                  <Button
                    type="link"
                    onClick={handleGenerateOtp}
                    disabled={otpLoading}
                    style={{
                      color: styles.chipText,
                      fontWeight: 500,
                      fontSize: "13px",
                    }}
                  >
                    Resend OTP
                  </Button>
                </div>
              </div>
            )}

            {otpError && (
              <div
                style={{
                  marginTop: "16px",
                  padding: "12px 16px",
                  background: styles.errorBg || "#fef2f2",
                  color: styles.errorColor || "#ef4444",
                  borderRadius: "10px",
                  fontSize: "13px",
                  textAlign: "center",
                  border: "1px solid " + (styles.errorBorder || "#fca5a5"),
                }}
              >
                {otpError}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (invalidToken) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: styles.secondaryBg,
        }}
      >
        <div
          style={{
            textAlign: "center",
            padding: "48px",
            background: styles.cardBg,
            borderRadius: "16px",
            boxShadow: "0 10px 25px rgba(0,0,0,0.05)",
            maxWidth: "400px",
          }}
        >
          <CloseOutlined
            style={{
              fontSize: "48px",
              color: "#ef4444",
            }}
          />

          <h2
            style={{
              marginTop: "24px",
              color: styles.textTitle,
            }}
          >
            Invalid Link
          </h2>

          <Text
            type="secondary"
            style={{
              fontSize: "15px",
              color: styles.textSecondary,
            }}
          >
            This setup link is no longer valid or has expired.
          </Text>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: styles.secondaryBg,
          padding: "24px",
          fontFamily:
            "'Google Sans Flex', 'Google Sans', 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, sans-serif",
        }}
      >
        <div
          style={{
            textAlign: "center",
            padding: "48px 36px",
            background: styles.cardBg,
            borderRadius: "20px",
            boxShadow: "0 12px 36px rgba(15, 23, 42, 0.06)",
            maxWidth: "480px",
            width: "100%",
          }}
        >
          <div
            style={{
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              backgroundColor: "#fef3c7",
              border: "1px solid #fde68a",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px auto",
            }}
          >
            <ExclamationCircleFilled
              style={{
                fontSize: "32px",
                color: "#d97706",
              }}
            />
          </div>

          <h2
            style={{
              fontSize: "22px",
              fontWeight: 600,
              color: styles.textTitle,
              margin: "0 0 10px 0",
            }}
          >
            Data Under Review
          </h2>

          <Text
            style={{
              fontSize: "14.5px",
              color: styles.textSecondary,
              lineHeight: "1.6",
              display: "block",
            }}
          >
            The data provided is under review by our team. We will get back to
            you shortly.
          </Text>
        </div>
      </div>
    );
  }

  const rendererStyles = {
    textPrimary: styles.textPrimary,
    textSecondary: styles.textSecondary,
    borderLightColor: styles.borderLightColor,
    chipText: styles.chipText,
    cardBg: styles.cardBg,
  };

  const customRenderers: CustomRenderers = {
    logoUpload: () => renderAssetDragger("logoUrl", "logo"),
    faviconUpload: () => renderAssetDragger("faviconUrl", "favicon"),
    invoiceHeaderTemplate: () =>
      renderTemplateSection("header", "invoiceSampleHeaderUrl"),
    invoiceFooterTemplate: () =>
      renderTemplateSection("footer", "invoiceSampleFooterUrl"),
    dataImportFile: renderDataImportFileField,
  };

  return (
    <div
      className="public-portal-root"
      style={{
        height: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
        backgroundColor: styles.pageBg,
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        fontFamily:
          "'Google Sans Flex', 'Google Sans', 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, sans-serif",
      }}
    >
      <style>{`
        .ant-upload-wrapper .ant-upload-drag {
          height: 190px !important;
          min-height: 190px !important;
          max-height: 190px !important;
          padding: 0 !important;
          background: #fafcff !important;
          border-color: #cbd5e1 !important;
        }
        .ant-upload-wrapper .ant-upload-drag .ant-upload-btn {
          padding: 0 !important;
          height: 100% !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        .ant-upload-wrapper .ant-upload-drag .ant-upload-drag-container {
          display: flex !important;
          flex-direction: column !important;
          align-items: center !important;
          justify-content: center !important;
          width: 100% !important;
          height: 100% !important;
        }
        .ant-input-group-addon {
          background-color: #f3f4f6 !important;
          color: #475569 !important;
          border-color: #cbd5e1 !important;
        }
        .custom-card-scroll {
          scrollbar-width: thin;
          scrollbar-color: transparent transparent;
          transition: scrollbar-color 0.2s ease-in-out;
        }
        .custom-card-scroll:hover {
          scrollbar-color: rgba(148, 163, 184, 0.4) transparent;
        }
        .custom-card-scroll::-webkit-scrollbar {
          width: 4px;
        }
        .custom-card-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-card-scroll::-webkit-scrollbar-thumb {
          background: transparent;
          border-radius: 4px;
          transition: background-color 0.2s ease-in-out;
        }
        .custom-card-scroll:hover::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.4);
        }
        @media (max-width: 1050px) {
          .public-portal-root {
            height: auto !important;
            max-height: none !important;
            min-height: 100vh !important;
            overflow-y: auto !important;
          }
          .portal-main-container {
            padding: 8px 16px 16px 16px !important;
            overflow: visible !important;
          }
          .portal-header-container, .portal-tracker-container {
            padding-left: 16px !important;
            padding-right: 16px !important;
          }
          .portal-body-wrapper {
            flex-direction: column !important;
            gap: 12px !important;
            overflow: visible !important;
          }
          .portal-sidebar-col {
            width: 100% !important;
            max-height: 220px !important;
          }
          .custom-card-scroll {
            max-height: none !important;
            overflow-y: visible !important;
          }
        }
        @media (max-height: 750px) {
          .public-portal-root {
            height: auto !important;
            max-height: none !important;
            min-height: 100vh !important;
            overflow-y: auto !important;
          }
          .portal-main-container {
            overflow: visible !important;
          }
          .portal-body-wrapper {
            overflow: visible !important;
          }
          .custom-card-scroll {
            max-height: none !important;
            overflow-y: visible !important;
          }
        }
      `}</style>

      <div
        className="portal-header-container"
        style={{
          width: "100%",
          maxWidth: "1280px",
          margin: "12px auto 0 auto",
          boxSizing: "border-box",
          flexShrink: 0,
        }}
      >
        <header
          style={{
            height: "60px",
            backgroundColor: styles.cardBg,
            borderRadius: "14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 20px",
            boxSizing: "border-box",
            boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
          }}
        >
          {/* Logo on Left */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flexShrink: 0,
            }}
          >
            <img
              src={logoImg}
              alt="acting office"
              style={{
                height: "36px",
                objectFit: "contain",
              }}
            />
          </div>

          {/* Stepper Pills Centered */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {FORM_STEPS.map((step, idx) => {
              const isCompleted = currentStep > step.id;
              const isActive = currentStep === step.id;

              return (
                <React.Fragment key={step.id}>
                  {idx > 0 && (
                    <RightOutlined
                      style={{
                        color: "#cbd5e1",
                        fontSize: "11px",
                        margin: "0 6px",
                      }}
                    />
                  )}

                  <div
                    onClick={() => handleStepChange(step.id)}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "6px 16px",
                      borderRadius: "20px",
                      fontSize: "13px",
                      fontWeight: isActive ? 600 : 500,
                      cursor: "pointer",
                      transition: "all 0.2s ease-in-out",
                      userSelect: "none",
                      border: "none",
                      backgroundColor: isActive
                        ? "#f0f9ff"
                        : isCompleted
                          ? "#ffffff"
                          : "#f8fafc",
                      color: isActive
                        ? styles.chipText
                        : isCompleted
                          ? "#0f172a"
                          : styles.textSecondary,
                      boxShadow: isActive
                        ? "0 2px 8px rgba(2, 132, 199, 0.12)"
                        : "none",
                    }}
                  >
                    <span
                      style={{
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "11px",
                        fontWeight: 700,
                        backgroundColor: isActive
                          ? styles.btnPrimaryBg
                          : isCompleted
                            ? "#dcfce7"
                            : "#e2e8f0",
                        color: isActive
                          ? "#ffffff"
                          : isCompleted
                            ? "#16a34a"
                            : "#64748B",
                        transition: "all 0.2s ease-in-out",
                      }}
                    >
                      {isCompleted ? (
                        <CheckOutlined style={{ fontSize: "10px" }} />
                      ) : (
                        step.id
                      )}
                    </span>

                    <span>{step.label}</span>
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          {/* Requested Info on Right */}
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div
              style={{
                fontSize: "13px",
                fontWeight: 600,
                color: styles.textTitle,
                whiteSpace: "nowrap",
              }}
            >
              Requested Information
            </div>

            <div
              style={{
                fontSize: "11px",
                color: styles.textSecondary,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: "4px",
                marginTop: "2px",
                whiteSpace: "nowrap",
              }}
            >
              <LockOutlined style={{ fontSize: "10px" }} />
              Protected workspace
            </div>
          </div>
        </header>
      </div>

      <main
        className="portal-main-container"
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          padding: "16px 24px 16px 24px",
          maxWidth: "1280px",
          width: "100%",
          margin: "0 auto",
          overflow: "hidden",
        }}
      >
        <Form
          form={form}
          layout="vertical"
          preserve={true}
          requiredMark={false}
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
          onValuesChange={(changed) => {
            if (
              changed.emailProvider !== undefined &&
              changed.emailProvider !== EmailProviderType.MicrosoftOutlook
            ) {
              if (
                form.getFieldValue("userCreationMethod") ===
                UserCreationMethod.CreateViaSCIM
              ) {
                form.setFieldValue(
                  "userCreationMethod",
                  UserCreationMethod.UploadCsv,
                );
              }
              /*
              if (form.getFieldValue("preferredLoginMode") === LoginMode.SAML) {
                form.setFieldValue(
                  "preferredLoginMode",
                  LoginMode.UserIdPassword,
                );
              }
              */
            }

            if (changed.noReplyEmailOption === NoReplyEmailOption.Default) {
              form.setFieldValue("customNoReplyEmail", undefined);
            }
          }}
        >
          <div
            className="portal-body-wrapper"
            style={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              gap: "20px",
              alignItems: "stretch",
            }}
          >
            <div
              className="portal-sidebar-col"
              style={{
                width: "250px",
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                alignSelf: "flex-start",
                maxHeight: "100%",
                overflow: "hidden",
              }}
            >
              <aside
                className="custom-card-scroll"
                style={{
                  maxHeight: "100%",
                  overflowY: "auto",
                  background: styles.cardBg,
                  borderRadius: "14px",
                  padding: "0 10px 14px 10px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "2px",
                }}
              >
                <div
                  style={{
                    position: "sticky",
                    top: 0,
                    backgroundColor: styles.cardBg,
                    zIndex: 2,
                    fontSize: "15px",
                    fontWeight: 600,
                    color: styles.textTitle,
                    padding: "14px 8px 10px 8px",
                  }}
                >
                  {currentStep === 1 ? "Practice Setup" : "Data Imports"}
                </div>

                {FORM_STEPS.find((s) => s.id === currentStep)?.sections.map(
                  (section) => (
                    <Form.Item key={section.key} shouldUpdate noStyle>
                      {({ getFieldValue }) => {
                        const templateType =
                          section.fields?.[0]?.fileTemplateType;

                        if (templateType) {
                          const files =
                            getFieldValue("dataConversionFiles") || [];
                          const uploadedFile = files.find((f: any) => {
                            if (!typeMatches(f, templateType)) return false;
                            const hasActualFile = Boolean(
                              f.file?.path ||
                              f.file?.Path ||
                              f.File?.Path ||
                              f.File?.path ||
                              f.fileUrl ||
                              f.FileUrl ||
                              f.rawFile,
                            );
                            return hasActualFile;
                          });

                          let statusIndicator: React.ReactNode = null;
                          if (uploadedFile) {
                            const valStatus =
                              uploadedFile.validationStatus ||
                              uploadedFile.ValidationStatus ||
                              "Under Review";

                            if (
                              valStatus === "Passed" ||
                              valStatus === "Completed"
                            ) {
                              statusIndicator = (
                                <CheckOutlined
                                  style={{
                                    color: styles.successColor,
                                    fontSize: "12px",
                                  }}
                                />
                              );
                            } else if (valStatus === "Failed") {
                              statusIndicator = (
                                <CloseCircleFilled
                                  style={{
                                    color: styles.errorColor,
                                    fontSize: "12px",
                                  }}
                                />
                              );
                            } else {
                              statusIndicator = (
                                <SyncOutlined
                                  spin
                                  style={{
                                    color: styles.chipText,
                                    fontSize: "12px",
                                  }}
                                />
                              );
                            }
                          }

                          const formValues = getFieldValue([]);
                          const isRequired = isTemplateRequired(
                            templateType,
                            formValues,
                          );

                          return renderLeftMenuItem(
                            section.key,
                            section.label,
                            statusIndicator,
                            isRequired,
                          );
                        }

                        const formValues = getFieldValue([]);
                        const required = getRequiredFieldNames(
                          section.key,
                          formValues,
                        );
                        const complete =
                          required.length > 0 &&
                          required.every((field) => {
                            const value = getFieldValue(field);
                            return typeof value === "string"
                              ? value.trim().length > 0
                              : Boolean(value);
                          });

                        return renderLeftMenuItem(
                          section.key,
                          section.label,
                          complete,
                          section.required,
                        );
                      }}
                    </Form.Item>
                  ),
                )}
              </aside>
            </div>

            <div
              style={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <section
                className="custom-card-scroll"
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  background: styles.cardBg,
                  borderRadius: "16px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {(currentStep === 1 || currentStep === 2) &&
                  (() => {
                    const activeSection = FORM_STEPS.find(
                      (s) => s.id === currentStep,
                    )?.sections.find((s) => s.key === activeSubTab);

                    if (!activeSection) return null;

                    return (
                      <DynamicSection
                        config={activeSection}
                        styles={rendererStyles}
                        customRenderers={customRenderers}
                      />
                    );
                  })()}
              </section>

              <div
                style={{
                  marginTop: "14px",
                  backgroundColor: styles.cardBg,
                  borderRadius: "14px",
                  padding: "12px 20px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexShrink: 0,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                }}
              >
                <Button
                  onClick={() => saveDraft(false)}
                  loading={savingDraft}
                  style={{
                    borderRadius: "6px",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: styles.btnSecondaryText,
                    borderColor: styles.btnSecondaryBorder,
                    height: "36px",
                    padding: "0 18px",
                    background: styles.btnSecondaryBg,
                  }}
                >
                  Save for later
                </Button>

                <Space size="middle">
                  {(currentStep > 1 ||
                    (currentStep === 1 && activeSubTab !== "basic_info")) && (
                    <Button
                      onClick={handleBack}
                      style={{
                        borderRadius: "6px",
                        fontSize: "13px",
                        fontWeight: 500,
                        color: styles.btnSecondaryText,
                        borderColor: styles.btnSecondaryBorder,
                        height: "36px",
                        padding: "0 18px",
                        background: styles.btnSecondaryBg,
                      }}
                    >
                      Back
                    </Button>
                  )}

                  <Button
                    type="primary"
                    onClick={handleNext}
                    loading={submitting}
                    style={{
                      borderRadius: "6px",
                      fontSize: "13px",
                      fontWeight: 600,
                      backgroundColor: styles.btnPrimaryBg,
                      borderColor: styles.btnPrimaryBorder,
                      height: "36px",
                      padding: "0 28px",
                      boxShadow: "0 1px 2px rgba(2,132,199,0.2)",
                    }}
                  >
                    {currentStep === 2 &&
                    SECTION_ORDER.indexOf(activeSubTab) ===
                      SECTION_ORDER.length - 1
                      ? "Submit"
                      : "Next"}
                  </Button>
                </Space>
              </div>
            </div>
          </div>
        </Form>
      </main>
    </div>
  );
};

const PublicClientDataRequest: React.FC = () => {
  useEffect(() => {
    // Force light theme styles on body
    document.body.classList.remove("dark-theme");
    document.body.style.backgroundColor = "#F3F4F6";

    return () => {
      // Cleanup on unmount
      document.body.classList.remove("dark-theme");
      document.body.style.backgroundColor = "#F3F4F6";
    };
  }, []);

  return (
    <ConfigProvider theme={forcedLightTheme}>
      <PublicClientDataRequestContent />
    </ConfigProvider>
  );
};

export default PublicClientDataRequest;
